import { isNotNullish } from '@examdojo/core/util/nullish';
import { MarkCalculationMethod, MarkGroup, SolutionPath } from '@examdojo/models/markscheme-v2';
import {
  OptionsItem,
  QuestionHttpModel,
  QuestionImageStoreModel,
  QuestionItemType,
  QuestionItemTypeEnum,
  QuestionItemV2,
  QuestionLLMFeedback,
  QuestionVoteStoreModel,
  StemDifficultyCategory,
  StemGroupItemV2,
  StemItemV2,
  StimulusStoreModel,
} from '@examdojo/models/question';
import { UserUIModel } from '@examdojo/models/user';
import { EntityState } from '@examdojo/state';
import { TableInsertModel, TableUpdateModel } from '@examdojo/supabase';
import { Feedback } from '../question-user-feedback';
import { QuestionStemStoreModel, QuestionStemUIModel } from './stem.model';

export type QuestionItemQuestionImageStoreModel = QuestionImageStoreModel & {
  questionImageUrl: string | null;
};

export type QuestionItemQuestionImageUIModel = QuestionItemQuestionImageStoreModel;
export type StimulusUIModel = StimulusStoreModel;

export type QuestionItemV1 = StimulusStoreModel | QuestionStemStoreModel | QuestionItemQuestionImageStoreModel;

export type QuestionStoreModel = Omit<
  QuestionHttpModel,
  'sys_period' | 'author' | 'items' | 'markscheme_items' | 'llm_feedback' | 'question_items' | 'metadata'
> & {
  authorId: QuestionHttpModel['author'];
  /** V1 question items. */
  items: Array<{ item_id: number; item_type: QuestionItemType }>;
  llm_feedback: QuestionLLMFeedback;
  metadata: {
    calculator_allowed?: boolean;
    seed?: {
      initial_status?: string;
      expected_feedback?: Feedback[];
    };
  } | null;

  /** V2 question items. */
  question_items?: QuestionItemV2[];
};

export type QuestionUIModel = QuestionStoreModel & {
  author: UserUIModel | null;
};

export interface QuestionEntityStoreModel {
  question: QuestionStoreModel;
  /**
   * V1 question items.
   * @deprecated
   */
  items?: QuestionItemV1[];
  userVote: QuestionVoteStoreModel | null;
  id: number;
}

export type QuestionEntityUIModel = Omit<QuestionEntityStoreModel, 'question' | 'items'> & {
  question: QuestionUIModel;
  /** V1 question items. */
  items?: Array<StimulusUIModel | QuestionStemUIModel | QuestionItemQuestionImageUIModel>; // TODO: map?
};

export type QuestionEntityState = EntityState<QuestionEntityStoreModel, object, number>;

export const DEFAULT_DIFFICULTY: StemDifficultyCategory = 'MEDIUM';

export function calculateOldQuestionTotalMarks(items: QuestionEntityUIModel['items']): number {
  if (!items) {
    return 0;
  }

  return items.reduce((sum, item) => {
    if (item.type === 'stem') {
      return sum + item.totalMarks;
    }
    return sum;
  }, 0);
}

export function containsQuestionMarkschemeImage(questionItems: QuestionItemV1[]): boolean {
  const stems = questionItems.filter((item): item is QuestionStemStoreModel => item.type === 'stem');
  const stemMarkschemes = stems.map((stem) => stem.markscheme).filter(isNotNullish);
  return stemMarkschemes.some((markschemes) =>
    markschemes.some((markscheme) => {
      return markscheme.methods.some((method) => {
        // Check method's image
        if (method.image !== null) {
          return true;
        }

        // Check mark_groups in method
        return method.mark_groups.some((group) => {
          // Check group's image
          if (group.image !== null) {
            return true;
          }

          // Check mark_group_alternatives
          return group.mark_group_alternatives.some((alt) => {
            // Check alternative's image
            if (alt.image !== null) {
              return true;
            }

            // Finally check marks
            return alt.marks.some((mark) => mark.image !== null);
          });
        });
      });
    }),
  );
}

export function calculateQuestionTotalMarks(
  questionItems: QuestionItemV2[],
  markCalculationMethod: MarkCalculationMethod,
): number {
  return questionItems.reduce((sum, item) => sum + getMarksFromQuestionItem(item, markCalculationMethod), 0);
}

export function getDifficultyFromTotalMarks(totalMarks: number): StemDifficultyCategory {
  return totalMarks <= 8 ? 'EASY' : totalMarks <= 16 ? 'MEDIUM' : 'HARD';
}

export function getQuestionDifficulty(questionEntity?: QuestionStoreModel | null): StemDifficultyCategory {
  return questionEntity?.difficulty ?? DEFAULT_DIFFICULTY;
}

function getMarksFromQuestionItem(item: QuestionItemV2, markCalculationMethod: MarkCalculationMethod): number {
  switch (item.type) {
    case QuestionItemTypeEnum.Stimulus:
      return 0;

    case QuestionItemTypeEnum.Stem:
      return getMarksFromStem(item, markCalculationMethod);

    case QuestionItemTypeEnum.StemGroup:
      return getMarksFromStemGroup(item, markCalculationMethod);

    case QuestionItemTypeEnum.Options:
      return getMarksFromOptions(item);

    default:
      return 0;
  }
}

function getMarksFromStemGroup(stemGroup: StemGroupItemV2, markCalculationMethod: MarkCalculationMethod): number {
  return stemGroup.stems.reduce(
    (sum: number, stem: StemItemV2) => sum + getMarksFromStem(stem, markCalculationMethod),
    0,
  );
}

export function getMarksFromStem(stem: StemItemV2, markCalculationMethod: MarkCalculationMethod): number {
  if (!stem.markscheme) {
    return 0;
  }

  return calculateStemTotalMarks(stem.markscheme, markCalculationMethod);
}

function getMarksFromOptions(options: OptionsItem): number {
  return options.markscheme.marks;
}

function calculateStemTotalMarks(
  markscheme: { all_mark_groups: MarkGroup[]; solution_paths: SolutionPath[] },
  method: MarkCalculationMethod,
): number {
  if (markscheme.solution_paths.length === 0) {
    return 0;
  }

  switch (method) {
    case 'ADDITIVE':
      // ibdp: sum all marks from first solution path
      return calculateMarksFromSolutionPath(markscheme.all_mark_groups, markscheme.solution_paths[0]);

    case 'SELECTIVE':
      // hsc: take maximum marks from all mark groups
      const markGroupTotals = markscheme.all_mark_groups.map((group) => group.marks);
      return Math.max(...markGroupTotals);

    default:
      console.warn('Unknown mark calculation method', method);
      return calculateMarksFromSolutionPath(markscheme.all_mark_groups, markscheme.solution_paths[0]);
  }
}

function calculateMarksFromSolutionPath(allMarkGroups: MarkGroup[], solutionPath: SolutionPath): number {
  return solutionPath.mark_group_refs.reduce((sum, markGroupId) => {
    const markGroup = allMarkGroups.find((group) => group.id === markGroupId);
    return sum + (markGroup?.marks ?? 0);
  }, 0);
}

export const QUESTION_CREATE_ALLOWED_KEYS = [
  'status',
  'items',
  'metadata',
  'question_number',
  'question_items',
] satisfies Array<keyof TableInsertModel<'questions'>>;

export type QuestionCreateModel = Pick<TableInsertModel<'questions'>, (typeof QUESTION_CREATE_ALLOWED_KEYS)[number]>;

export const QUESTION_UPDATE_ALLOWED_KEYS = [...QUESTION_CREATE_ALLOWED_KEYS] satisfies Array<
  keyof TableUpdateModel<'questions'>
>;

export type QuestionUpdateModel = Pick<TableUpdateModel<'questions'>, (typeof QUESTION_UPDATE_ALLOWED_KEYS)[number]>;
